import { Alert, Box, Card, Divider, Group, Skeleton, Stack, Table, Text, Title } from "@mantine/core";
import { IconAlertTriangle, IconReceiptOff } from "@tabler/icons-react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { PlatformInvoiceApi } from "../../../apis";
import type { PlatformInvoice, PlatformInvoiceLineItem } from "../../../apis/platformInvoice";
import { EmptyState } from "../../../components/billing/EmptyState";
import { InvoiceStatusBadge } from "../../../components/billing/InvoiceStatusBadge";
import { MoneyCell } from "../../../components/billing/MoneyCell";
import { SectionHeader } from "../../../components/billing/SectionHeader";
import { PageLayout } from "../../../components/layout";
import { formatDate } from "../../../utils/format";

const heroWash = { background: "light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))" };

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useSWR(
    id ? `swr.billing.invoice.${id}` : null,
    () => PlatformInvoiceApi.get(id!)
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any;
  const invoice = raw?.invoice as PlatformInvoice | undefined;
  const lineItems = (raw?.lineItems ?? []) as PlatformInvoiceLineItem[];

  const breadcrumb = [
    { to: "/", label: "Эхлэл" },
    { to: "/billing/invoices", label: "Платформ нэхэмжлэх" },
    { label: invoice?.invoiceNumber ?? "…" },
  ];

  return (
    <PageLayout title={invoice?.invoiceNumber ?? "Нэхэмжлэх"} breadcrumb={breadcrumb}>
      {isLoading ? (
        <Skeleton height={220} radius="md" />
      ) : error ? (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>Нэхэмжлэх ачааллахад алдаа гарлаа.</Alert>
      ) : !invoice ? (
        <Card withBorder radius="md" padding="md">
          <EmptyState icon={<IconReceiptOff size={26} />} message="Нэхэмжлэх олдсонгүй." />
        </Card>
      ) : (
        <Stack gap="md">
          <Card withBorder padding="lg" radius="md" style={heroWash}>
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" style={{ letterSpacing: "0.06em" }}>Нэхэмжлэх</Text>
                <Title order={3} style={{ fontVariantNumeric: "tabular-nums" }}>{invoice.invoiceNumber}</Title>
                <Text size="sm" c="dimmed" mt={4} style={{ fontVariantNumeric: "tabular-nums" }}>
                  {formatDate(invoice.periodStart)} – {formatDate(invoice.periodEnd)}
                </Text>
              </Box>
              <InvoiceStatusBadge status={invoice.status} />
            </Group>
            <Divider my="md" />
            <Group gap="xl">
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Нийт</Text>
                <MoneyCell value={invoice.totalAmount} currency={invoice.currency} fw={700} />
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Төлсөн</Text>
                <MoneyCell value={invoice.amountPaid} currency={invoice.currency} fw={700} c="green.7" />
              </Box>
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" style={{ letterSpacing: "0.05em" }}>Үлдэгдэл</Text>
                <MoneyCell value={invoice.amountDue} currency={invoice.currency} fw={700} c="brand.7" />
              </Box>
            </Group>
          </Card>

          <Card withBorder padding="lg" radius="md">
            <Stack gap="md">
              <SectionHeader eyebrow="Задаргаа" title="Мөрүүд" />
              {lineItems.length === 0 ? (
                <EmptyState icon={<IconReceiptOff size={26} />} message="Мөр байхгүй байна." />
              ) : (
                <Table.ScrollContainer minWidth={520}>
                  <Table verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Тайлбар</Table.Th>
                        <Table.Th ta="right">Тоо</Table.Th>
                        <Table.Th ta="right">Нэгж үнэ</Table.Th>
                        <Table.Th ta="right">Дүн</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {lineItems.map((li) => (
                        <Table.Tr key={li.id}>
                          <Table.Td>{li.description}</Table.Td>
                          <Table.Td ta="right" style={{ fontVariantNumeric: "tabular-nums" }}>{li.quantity}</Table.Td>
                          <Table.Td ta="right"><MoneyCell value={li.unitPrice} currency={invoice.currency} /></Table.Td>
                          <Table.Td ta="right"><MoneyCell value={li.amount} currency={invoice.currency} /></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              )}
            </Stack>
          </Card>
        </Stack>
      )}
    </PageLayout>
  );
};

export default InvoiceDetail;
